import { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, get, useFormContext, useFormState, useWatch } from "react-hook-form";
import { StatusSwitch } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/controls";
import { textInputClassName } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { SelectMenuItems } from "@/app/components/ui/interface";
import { useOrgServicesListAPI } from "@/app/hooks/api_hooks/org/useOrgServicesListAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  GIFT_DETAILS_ID_LABEL,
  GIFT_DETAILS_TITLE,
  GIFT_PET_SPECIES_ITEMS,
  IGiftFormFields,
  toSelectItems,
  toServiceSelectItems,
} from "../interface";
import {
  ChipApiSearchSelect,
  ChipSearchSelect,
  ChipTextInput,
  FormField,
  GiftSelectField,
  InlineSwitchField,
  SectionCard,
  errorBorderClassName,
} from "./fields";
import { useGiftProductSearch } from "./useGiftProductSearch";
import { validateDiscountValue, validatePositiveInteger } from "./validators";

/** One fixed pet species row (Dog or Cat): a switch to include it, and the ids given for it. */
const ItemRow = ({
  index,
  speciesName,
  searchProducts,
  serviceItems,
  isServiceListLoading,
}: {
  index: number;
  speciesName: string;
  searchProducts: (query: string) => Promise<SelectMenuItems>;
  serviceItems: SelectMenuItems;
  isServiceListLoading: boolean;
}) => {
  const { control, getValues, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name: "items" });
  const [giftType, idNames, isEnabled] = useWatch({
    control,
    name: ["gift_type", "id_names", `items.${index}.enabled`],
  });
  const idsError: FieldError | undefined = get(errors, `items.${index}.ids`);
  const idLabel = GIFT_DETAILS_ID_LABEL[giftType] ?? "IDs";

  return (
    <div
      className={`grid grid-cols-1 items-start gap-3 rounded-lg border p-3 md:grid-cols-[200px_1fr_auto] ${
        isEnabled ? "border-slate-200 bg-slate-50/40" : "border-dashed border-slate-200 bg-white"
      }`}
    >
      <FormField label="Pet Species">
        <div className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 shadow-sm">
          <span className={`text-sm font-medium ${isEnabled ? "text-slate-700" : "text-slate-400"}`}>
            {speciesName}
          </span>
        </div>
      </FormField>
      <FormField label={idLabel} required={isEnabled} error={idsError?.message}>
        <Controller
          control={control}
          name={`items.${index}.ids`}
          rules={{
            // Kept mounted while the row is off so the "at least one species" rule is still checked.
            validate: (value, formValues) => {
              if (formValues.items[index]?.enabled)
                return value.length > 0 || `Add at least one ${idLabel.slice(0, -1)}.`;
              const isAnyRowOn = formValues.items.some((row) => row.enabled);
              return isAnyRowOn || index > 0 || "Switch on at least one pet species.";
            },
          }}
          render={({ field }) => {
            if (!isEnabled) {
              return (
                <div className="flex h-9 items-center rounded-lg border border-dashed border-slate-200 px-3 text-sm text-slate-400">
                  Not given for {speciesName}
                </div>
              );
            }
            const rememberName = (item: { id: string; title: string }) =>
              setValue("id_names", { ...getValues("id_names"), [item.id]: item.title });
            // Variants: inventory product search. Services: org services loaded at startup. BOM ids are typed in.
            if (giftType === "service") {
              return (
                <ChipSearchSelect
                  id={`items.${index}.ids`}
                  items={serviceItems}
                  value={field.value}
                  onChange={field.onChange}
                  names={idNames}
                  onPick={rememberName}
                  placeholder="Search service by name"
                  isLoading={isServiceListLoading}
                  hasError={Boolean(idsError)}
                />
              );
            }
            return giftType === "variant" ? (
              <ChipApiSearchSelect
                id={`items.${index}.ids`}
                value={field.value}
                onChange={field.onChange}
                search={searchProducts}
                names={idNames}
                onPick={rememberName}
                placeholder="Search product by name (min 3 letters)"
                hasError={Boolean(idsError)}
              />
            ) : (
              <ChipTextInput
                id={`items.${index}.ids`}
                value={field.value}
                onChange={field.onChange}
                placeholder="Type or paste an ID and press Enter"
                getLabel={(item) => idNames[item] || item}
                hasError={Boolean(idsError)}
              />
            );
          }}
        />
      </FormField>
      <div className="flex h-9 items-center md:mt-[22px]">
        <StatusSwitch
          checked={isEnabled}
          label={`Give for ${speciesName}`}
          onRequestChange={(next) =>
            setValue(`items.${index}.enabled`, next, { shouldDirty: true, shouldValidate: true })
          }
        />
      </div>
    </div>
  );
};

/** Rows for the gift type picked in General Details: variant, BOM or service ids per pet species. */
const GiftDetailsRows = () => {
  const { control, getFieldState, getValues, register, trigger } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name: "coupon_id" });
  const itemsEnabled = useWatch({ control, name: "items" })
    .map((row) => row.enabled)
    .join(",");
  const giftType = useWatch({ control, name: "gift_type" });
  const searchProducts = useGiftProductSearch();
  const orgServices = useAppSelector((state) => state.staticAPI.org_services);
  const fetchOrgServices = useOrgServicesListAPI();
  const [isServiceListLoading, setIsServiceListLoading] = useState<boolean>(false);
  const serviceItems = useMemo(() => toServiceSelectItems(orgServices), [orgServices]);

  // Org services normally load with VMS; fetch them here only if that has not happened yet.
  useEffect(() => {
    if (giftType !== "service" || orgServices?.length) return;
    setIsServiceListLoading(true);
    Promise.resolve(fetchOrgServices()).finally(() => setIsServiceListLoading(false));
  }, [giftType, orgServices?.length, fetchOrgServices]);
  const isServiceGift = giftType === "service";
  // Switching a row on/off changes which rows need ids and whether any species is left on. Re-check the rows
  // when they already show an error or when every species is off; a fresh form is not flagged before input.
  useEffect(() => {
    const isEveryRowOff = getValues("items").every((row) => !row.enabled);
    if (isEveryRowOff || getFieldState("items").invalid) trigger("items");
  }, [itemsEnabled, getFieldState, getValues, trigger]);

  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-900">{GIFT_DETAILS_TITLE[giftType] ?? "Gift Details"}</p>
        <p className="text-xs text-slate-500">
          Switch on each pet species the gift is for and add what is given. Change the type in General Details.
        </p>
      </div>

      <div className="mb-3 max-w-sm">
        <FormField label="Coupon ID" htmlFor="coupon_id" required={isServiceGift} error={errors.coupon_id?.message}>
          <input
            id="coupon_id"
            type="text"
            placeholder="Coupon ID"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.coupon_id))}`}
            {...register("coupon_id", {
              // Optional for every type, but the backend requires a coupon on service gifts.
              validate: (value, formValues) =>
                formValues.gift_type !== "service" ||
                Boolean(value.trim()) ||
                "Coupon ID is required for service gifts.",
            })}
          />
        </FormField>
      </div>

      <div className="space-y-3">
        {/* Rows follow GIFT_PET_SPECIES_ITEMS order, the same order formUtils builds `items` in. */}
        {GIFT_PET_SPECIES_ITEMS.map((species, index) => (
          <ItemRow
            key={species.id}
            index={index}
            speciesName={species.title}
            searchProducts={searchProducts}
            serviceItems={serviceItems}
            isServiceListLoading={isServiceListLoading}
          />
        ))}
      </div>
    </div>
  );
};

export const GiftItemsCard = ({
  allowedDiscountTypes,
  isFormatLoading,
}: {
  allowedDiscountTypes: string[];
  isFormatLoading: boolean;
}) => {
  const { control, getFieldState, register, setValue, trigger } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control });
  const [isAutoAddition, discountType] = useWatch({ control, name: ["is_auto_addition", "discount_type"] });
  const discountTypeItems = useMemo(() => toSelectItems(allowedDiscountTypes), [allowedDiscountTypes]);

  // The allowed range depends on the discount type, so re-check a value the user already typed.
  useEffect(() => {
    if (getFieldState("discount_value").isDirty) trigger("discount_value");
  }, [discountType, getFieldState, trigger]);

  return (
    <SectionCard title="Gift Details" description="What the customer receives, per pet species, and at what price.">
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
        <FormField
          label="Max Quantity"
          htmlFor="max_quantity_count"
          required
          error={errors.max_quantity_count?.message}
        >
          <input
            id="max_quantity_count"
            type="number"
            min={1}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.max_quantity_count))}`}
            {...register("max_quantity_count", { validate: validatePositiveInteger("Max quantity") })}
          />
        </FormField>
        <GiftSelectField
          name="discount_type"
          label="Discount Type"
          required
          items={discountTypeItems}
          isLoading={isFormatLoading}
          placeholder="Select discount type"
        />
        <FormField label="Discount Value" htmlFor="discount_value" required error={errors.discount_value?.message}>
          <input
            id="discount_value"
            type="number"
            min={0}
            max={discountType === "Percentage" ? 100 : undefined}
            step={1}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.discount_value))}`}
            {...register("discount_value", {
              validate: (value, formValues) => validateDiscountValue(formValues.discount_type === "Percentage")(value),
            })}
          />
        </FormField>
        <InlineSwitchField
          label="Auto Add to Cart"
          checked={isAutoAddition}
          onChange={(next) => setValue("is_auto_addition", next, { shouldDirty: true })}
        />
      </div>

      <GiftDetailsRows />
    </SectionCard>
  );
};
